import { graphql } from "gatsby";
import * as React from "react";

import { useContext } from "react";
import { CityLinks } from "../components/destination/city-links";
import { Faq } from "../components/destination/faq";
import { DestinationPageHero } from "../components/destination/hero";
import { DestinationOverviewProps } from "../components/destination/overview";
import { RegionResortCore } from "../components/destination/region-resort-core";
import { Layout } from "../components/layout/layout";
import { ShareContent } from "../components/layout/share";
import { DestinationCards } from "../components/shared/cards";
import { SunheronFeatures } from "../components/shared/sh-features";
import { TemperatureUnitContext } from "../context/app-context";
import { useLoc } from "../hooks/useLoc";
import { useMonth } from "../hooks/useMonth";
import { LocationType } from "../interfaces";
import { monthSlugs } from "../services/centralStore";
import textParser from "../services/textParser";
import { City } from "./fragments/city";

interface CityType {
    data: Query;
    location: LocationType;
    pageContext: {
        fullSlug: string;
        slug: string;
        year: number;
    };
}

const CityPage = ({ data, pageContext }: CityType) => {
    //console.log(pageContext);
    const loc = useLoc();
    const [temperatureUnit] = useContext(TemperatureUnitContext);
    const { month } = useMonth({});
    const description = textParser({
        text: data.citiesJson.header,
        units: temperatureUnit,
        noNbsp: true
    });

    const weatherOverviewText = `${loc("common.weatherOverview")} ${loc(
        "common.for"
    )} ${data.citiesJson.name}`;
    const closestResortsText = `${loc("common.closestDestinationsFor")} ${
        data.citiesJson.name
    }`;
    const closestCitiesText = `${loc("common.closestCitiesFor")} ${
        data.citiesJson.name
    }`;

    const overviewProperties: DestinationOverviewProps = {
        weatherOverviewText,
        similarDestinationsText: closestResortsText,
        includeFaqs: true
    };

    let faqs: { answer: string; question: string }[];
    let monthSentences: { answer: string; question: string }[];
    faqs = data.citiesJson.monthData
        .filter((m) => m.questions.length)
        .map((m) => {
            return { question: m.questions[0], answer: m.answers[0] };
        });
    monthSentences = data.citiesJson.monthData.map((m, idx) => {
        const monthName = loc("common.months." + monthSlugs[idx]);
        return {
            question: `${monthName}  ${loc("common.weather")}`,
            answer: m.header
        };
    });
    monthSentences.splice(0, 0, {
        question: loc("common.weatherOverview"),
        answer: data.citiesJson.header
    });
    const header = `${data.citiesJson.name} ${loc(
        "common.weatherAndClimate"
    )} ${loc("common.in")} ${pageContext.year}`;

    return (
        <Layout
            path={"/" + pageContext.fullSlug}
            title={header}
            description={``}
        >
            <DestinationPageHero header={header} city={data.citiesJson} />
            <RegionResortCore
                {...{ pageContext, month }}
                locationJson={data.citiesJson}
                overviewProperties={overviewProperties}
                introContent={description}
                introTitle={data.citiesJson.name}
            />

            <div className="h-16"></div>

            <h2 className="w-destination-header font-destination-header m-auto mb-12">
                <a
                    id="destinations-similar"
                    className="relative top-[-100px] md:top-[-120px]"
                />
                {closestResortsText}
            </h2>
            <DestinationCards
                regionsOrResorts={data.citiesJson.closestResorts}
                isResorts={true}
                includeCountry={true}
                className=""
            />
            <CityLinks
                cities={data.citiesJson.closestCities}
                headerText={closestCitiesText}
            />

            <hr className="m-auto mt-16 mb-12 w-full xm:w-3/4" />

            <h2 className="w-destination-header font-destination-header m-auto mt-16">
                <a
                    id="weather-overview"
                    className="relative top-[-100px] md:top-[-120px]"
                />
                {weatherOverviewText}
            </h2>
            <Faq faqs={monthSentences}></Faq>
            <h2 className="w-destination-header font-destination-header m-auto mt-16">
                <a
                    id="faqs"
                    className="relative top-[-100px] md:top-[-120px]"
                />
                {`${loc("common.faqs")}`}
            </h2>
            <Faq faqs={faqs}></Faq>
            <div className="h-20"></div>
            <SunheronFeatures />
            <div className="h-20"></div>
            <ShareContent active={true} partOfPage={true} />
        </Layout>
    );
};

interface Query {
    citiesJson: City;
}

export const query = graphql`
    query CitiesJson($slug: String) {
        citiesJson(slug: { eq: $slug }) {
            ...CityFull
        }
    }
`;

export default CityPage;
